import { useChatActions, useChatStoreApi } from '@ai-sdk-tools/store';
import { useMutation } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { cn, PopoverContent } from '@newfront-insurance/core-ui/v2';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { graphql } from '@newfront-insurance/graphql-frontend';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { motion } from 'framer-motion';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useRef, useState } from 'react';

import type { UIMessageWithMetadata } from '../../../lib/types';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import { useNovaContext } from '../hooks/use-chat-context';
import { NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../lib/analytics';
import { buildNovaRequestContext } from '../lib/request-context';
import { isDurableNovaChatCompletionsEndpoint } from '../lib/transport';

const UPSERT_MESSAGE_FEEDBACK = graphql(`
  mutation UpsertNovaMessageFeedback($messageUuid: String!, $input: MessageFeedbackInput!) {
    upsertMessageFeedback(messageUuid: $messageUuid, input: $input) {
      uuid
      messageUuid
      reason
      stage
      detail
    }
  }
`);

export const FEEDBACK_REGENERATION_FLAG = 'nova-feedback-regeneration';

const FEEDBACK_REASONS = [
  { value: 'SOMETHING_BROKE', label: 'Something broke' },
  { value: 'INCOMPLETE', label: 'Incomplete' },
  { value: 'SOURCES_WRONG_OR_MISSING', label: 'Sources wrong or missing' },
  { value: 'DIDNT_FOLLOW_INSTRUCTIONS', label: "Didn't follow instructions" },
  { value: 'OUTDATED', label: 'Outdated' },
  { value: 'INACCURATE', label: 'Inaccurate' },
] as const;

const FEEDBACK_ACTION_GRADIENT = 'bg-[linear-gradient(150deg,#97ECE3_2%,#DBC1F4_82%)]';

type FeedbackReason = (typeof FEEDBACK_REASONS)[number]['value'];

interface MessageFeedbackPopoverProps {
  message: UIMessageWithMetadata;
  open: boolean;
  isReactionSaving: boolean;
  onOpenChange: (open: boolean) => void;
  waitForReactionPersistence: () => Promise<void>;
}

export function MessageFeedbackPopover({
  message,
  open,
  isReactionSaving,
  onOpenChange,
  waitForReactionPersistence,
}: MessageFeedbackPopoverProps) {
  const firstReasonRef = useRef<HTMLButtonElement>(null);
  const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());
  const [selectedReason, setSelectedReason] = useState<FeedbackReason>();
  const [detail, setDetail] = useState('');
  const [isSavingReason, setIsSavingReason] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [upsertFeedback] = useMutation(UPSERT_MESSAGE_FEEDBACK, {
    context: { notifications: { preventErrorNotification: true } },
  });
  const { regenerate } = useChatActions<UIMessageWithMetadata>();
  const chatStoreApi = useChatStoreApi<UIMessageWithMetadata>();
  const { accountUuid, classUuid } = useNovaContext();
  const overlay = useChatOverlayContext();
  const notifications = useNotifications();
  const analytics = useAnalytics();
  const regenerationFlagEnabled = useFeatureFlag(FEEDBACK_REGENERATION_FLAG, false);
  const regenerationEnabled = regenerationFlagEnabled && isDurableNovaChatCompletionsEndpoint(overlay.apiEndpoint);
  const isProblemReport = selectedReason === 'SOMETHING_BROKE';

  const persistFeedback = async (reason: FeedbackReason, stage: 'CAPTURED' | 'SUBMITTED', nextDetail?: string) => {
    await waitForReactionPersistence();
    const result = await upsertFeedback({
      variables: {
        messageUuid: message.id,
        input: {
          reason,
          stage,
          detail: nextDetail?.trim() || null,
        },
      },
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
    return result;
  };

  const captureReason = (reason: FeedbackReason) => {
    setSelectedReason(reason);
    setDetail('');
    setError(undefined);
    setIsSavingReason(true);
    const save = saveQueueRef.current
      .catch(() => undefined)
      .then(() => persistFeedback(reason, 'CAPTURED'))
      .then(() => {
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REASON_CAPTURED, {
          messageUuid: message.id,
          reason,
        });
      })
      .catch(() => setError("We couldn't save that selection. Try again."))
      .finally(() => setIsSavingReason(false));
    saveQueueRef.current = save;
  };

  const handleSubmit = async () => {
    if (!selectedReason || isSubmitting) return;

    setError(undefined);
    setIsSubmitting(true);
    try {
      await saveQueueRef.current.catch(() => undefined);
      const result = await persistFeedback(selectedReason, 'SUBMITTED', detail);
      const feedbackUuid = result.data?.upsertMessageFeedback.uuid;
      if (!feedbackUuid) throw new Error('Feedback save returned no identifier');

      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_SUBMITTED, {
        hasDetail: Boolean(detail.trim()),
        messageUuid: message.id,
        reason: selectedReason,
      });

      if (isProblemReport) {
        notifications.add({
          type: 'success',
          duration: 3000,
          title: "Thanks, we're on it. We'll email you when it's fixed.",
        });
        onOpenChange(false);
        return;
      }

      if (!regenerationEnabled) {
        notifications.add({ type: 'success', duration: 3000, title: 'Thanks, your feedback has been saved.' });
        onOpenChange(false);
        return;
      }

      onOpenChange(false);
      const startedAt = performance.now();
      const targetChatUuid = overlay.chatUuid;
      try {
        const payloadAdditionalContext = Object.fromEntries(
          Object.entries(overlay.additionalContext ?? {}).filter(([key]) => key !== 'messageMetadata'),
        );
        const requestContext = await buildNovaRequestContext({
          accountUuid,
          additionalContext: payloadAdditionalContext,
          classUuid,
        });

        overlay.beginRegeneration(targetChatUuid, message);
        let streamError: unknown;
        try {
          await regenerate({
            messageId: message.id,
            body: {
              ...requestContext,
              regeneration: {
                targetMessageUuid: message.id,
                feedbackUuid,
              },
            },
          });
        } catch (caughtError) {
          streamError = caughtError;
        }

        // The external store mirrors the AI SDK state on a later frame and can still contain
        // an error from an earlier request. The durable backend snapshot is authoritative, and
        // also lets us recover when the stream disconnects after the variant was persisted.
        const canonicalMessages = await overlay.refetchMessages(targetChatUuid);
        if (canonicalMessages && overlay.isActiveChat(targetChatUuid)) {
          const chatState = chatStoreApi.getState();
          chatState.clearTransientDataParts();
          chatState.setNewChat(targetChatUuid, canonicalMessages);
          chatState.setMessages(canonicalMessages);
        }
        const persistedVariant = canonicalMessages?.some((candidate) => {
          const regeneration = candidate.metadata?.regeneration;
          return regeneration?.sourceMessageUuid === message.id && regeneration.feedbackUuid === feedbackUuid;
        });
        if (!canonicalMessages || !persistedVariant) {
          throw streamError ?? new Error('Regenerated answer was not present in the canonical chat snapshot');
        }
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REGENERATION_SUCCEEDED, {
          latencyMs: Math.round(performance.now() - startedAt),
          messageUuid: message.id,
          reason: selectedReason,
        });
      } catch {
        if (overlay.isActiveChat(targetChatUuid)) {
          notifications.add({ type: 'error', title: 'Nova could not fix this answer. Please try again.' });
        }
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.MESSAGE_FEEDBACK_REGENERATION_FAILED, {
          messageUuid: message.id,
          reason: selectedReason,
        });
      } finally {
        overlay.clearPendingRegeneration(targetChatUuid);
      }
    } catch {
      setError(isProblemReport ? 'The problem report could not be sent. Try again.' : 'Feedback could not be saved.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <PopoverContent
      role="dialog"
      aria-label="What went wrong?"
      aria-modal="false"
      side="bottom"
      sideOffset={8}
      align="start"
      collisionPadding={16}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        firstReasonRef.current?.focus();
      }}
      className="border-steel-200 z-[1500] max-h-[calc(100vh-2rem)] w-[min(28.75rem,calc(100vw-2rem))] overflow-auto rounded-[0.25rem] border bg-white p-0 shadow-xl"
    >
      <div className="p-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm leading-5 font-semibold text-black">What went wrong?</h3>
          <span className="text-steel-400 inline-flex items-center gap-1 text-xs" role="status">
            {(isReactionSaving || isSavingReason) && (
              <>
                <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                Saving
              </>
            )}
          </span>
        </div>
        <div
          className="mt-2.5 flex flex-wrap gap-x-0.5 gap-y-2.5"
          role="radiogroup"
          aria-label="Feedback reason"
          onKeyDown={(event) => {
            if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
            const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]'));
            const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
            if (currentIndex < 0) return;
            event.preventDefault();
            const nextIndex =
              event.key === 'Home'
                ? 0
                : event.key === 'End'
                  ? buttons.length - 1
                  : (currentIndex + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + buttons.length) %
                    buttons.length;
            buttons[nextIndex]?.focus();
          }}
        >
          {FEEDBACK_REASONS.map((reason, index) => (
            <button
              key={reason.value}
              ref={index === 0 ? firstReasonRef : undefined}
              type="button"
              role="radio"
              aria-checked={selectedReason === reason.value}
              onClick={() => captureReason(reason.value)}
              className={cn(
                'h-8 rounded-full border px-3 text-sm leading-5 transition-opacity',
                'focus-visible:outline-wtw-infinity-500 focus-visible:outline-2 focus-visible:outline-offset-2',
                selectedReason === reason.value
                  ? cn('border-transparent text-[#350E5E]', FEEDBACK_ACTION_GRADIENT)
                  : 'border-steel-200 text-steel-400 hover:border-steel-300 bg-white',
              )}
            >
              {reason.label}
            </button>
          ))}
        </div>
      </div>

      {selectedReason && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="border-steel-200 mt-2.5 border-t p-4"
        >
          <label htmlFor={`nova-feedback-detail-${message.id}`} className="text-sm leading-5 font-semibold text-black">
            {isProblemReport ? 'What happened?' : 'What would make it better? (optional)'}
          </label>
          <textarea
            id={`nova-feedback-detail-${message.id}`}
            value={detail}
            rows={3}
            maxLength={4000}
            onChange={(event) => {
              setDetail(event.target.value);
              setError(undefined);
            }}
            placeholder={
              isProblemReport
                ? 'e.g. The response stopped generating halfway through'
                : regenerationEnabled
                  ? 'Nova will rewrite this answer using your feedback.'
                  : 'Tell us how this answer could be improved.'
            }
            className={cn(
              'border-steel-200 text-steel-500 placeholder:text-steel-300 mt-2.5 h-[6.25rem] w-full resize-none',
              'focus:border-steel-400 rounded-[0.75rem] border p-3 text-sm leading-5 outline-none',
            )}
          />
          {error && (
            <div className="mt-2 flex items-center justify-between gap-3" role="alert">
              <p className="text-fire-600 text-xs">{error}</p>
              {selectedReason && error.includes("couldn't save") && (
                <button
                  type="button"
                  className="text-brand-400 text-xs font-medium"
                  onClick={() => captureReason(selectedReason)}
                >
                  Retry
                </button>
              )}
            </div>
          )}
          <div className="mt-2.5 flex justify-end">
            <button
              type="button"
              disabled={isSubmitting || isSavingReason}
              onClick={() => void handleSubmit()}
              className={cn(
                'inline-flex h-9 min-w-[10.375rem] items-center justify-center gap-2 rounded-full px-4',
                'text-sm leading-5 font-semibold text-[#350E5E] transition-opacity hover:opacity-90',
                'disabled:cursor-not-allowed disabled:opacity-50',
                FEEDBACK_ACTION_GRADIENT,
              )}
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowRight className="size-4" aria-hidden="true" />
              )}
              {isProblemReport ? 'Report problem' : regenerationEnabled ? 'Fix this answer' : 'Submit feedback'}
            </button>
          </div>
        </motion.div>
      )}
    </PopoverContent>
  );
}
